<script setup lang="ts">
import {
  computed,
  DefineComponent,
  defineComponent,
  nextTick,
  onMounted,
  PropType,
  ref,
  watch,
} from 'vue';
import { OptionFilterEnum } from '../../enums';
import FilterGlobalStatusesParameter from './components/FilterGlobalStatusesParameter.vue';
import { FilterOptionModalItem } from '../../models/FilterOptionModalItem';
import { OptionItemModel } from '../../models';
import { TirCheckbox, TirPmRadio, TirPmScrollbar } from 'tir-components';
import {
  FilterOptions,
  GetGlobalStatusesItem,
  PageRequestModel,
  SelectedFilterOptions,
} from '@/components/tasks-kanban/models';
import FilterParameterItem from './components/FilterParameterItem.vue';
import { AdapterService } from './adapter';
import { TaskFilterStatusEnum } from '@/components/tasks-kanban/enums';
import ProjectSearchRequestModel from '@/components/sidebar/models/ProjectSearchRequestModel';
import { ProjectModel } from '@/models';
import { debounce } from 'tir-pm-debounce';
import { TagModel } from '@/components/task-forms/models';
import {
  BaseProjectResponse,
  GetListTaskTypeRequest,
  GetTagsListDetailRequest,
  NamespaceModel,
  TotalCountElementsItemFilter,
} from './adapter/models';
import ProjectNamespaceListRequest, {
  GetDifficultyListDetailRequest,
  GetDifficultyListItemDetail,
  GetPrioritiesListDetailRequest,
  GetStatusesListDetailItem,
  GetStatusesListDetailRequest,
  PageRequest,
  ProjectQueueTaskListItem,
  ProjectQueueTaskListRequest,
} from '@/components/tasks-kanban/adapter/TaskKanbanAdapterService';
import { TypeModel } from '@/pages/namespace-types-settings-page/models';
import { TaskParameterTypeEnum } from '@/components/task-parameter';
import GetListStatusRequest from './adapter/models/GetListStatusRequest';
import EnumProjectType from '@/api/Main/dataSource/api/projects/base/entities/enums/EnumProjectType';

/**Адаптер */
const adapter = new AdapterService();

/**Параметры */
const props = defineProps({
  /**Выбранные глобальные статусы */
  selectedGlobalStatuses: {
    type: Array as PropType<OptionItemModel[]>,
  },
  /**Тип параметра */
  typeParameter: {
    type: Object as PropType<TaskParameterTypeEnum>,
  },
  /**Отображаемый компонент */
  showComponent: {
    type: Object as () => DefineComponent<{}, {}, any>,
    required: true,
  },
  /**Данные опции фильтра */
  filterOptionData: {
    type: Object as PropType<FilterOptionModalItem>,
    required: true,
  },
  /**Опция */
  option: {
    type: Object as PropType<OptionFilterEnum>,
    required: true,
  },
  /**Поле поиска */
  searchField: {
    type: String,
  },
  /**id выбранного проекта */
  idSelectedProject: { type: Number },
  /**id выбранного НС */
  idSelectedNamespace: { type: String },
  /**id выбранной очереди */
  idSelectedQueue: { type: String },
  /**показать только где я админ для проектов точно, для областей, для очередей ...' */
  showOnlyCurrentIsAdmin: {
    type: Boolean,
    required: false,
    default: true,
  },
  /**Выбранные элементы фильтра */
  selectedOptionsFilter: {
    type: Object as PropType<SelectedFilterOptions>,
  },
});

/**События */
const emit = defineEmits<{
  /**Переключение чекбокса элемента*/
  toggleItem: [item: OptionItemModel, type: OptionFilterEnum, isMultiSelect: boolean];
  /**Выбрать все элементы */
  selectAllElements: [type: OptionFilterEnum, isMultiSelect: boolean];
  /**Прокрут скрола до конца */
  scrollFinished: [type: OptionFilterEnum];
  /**Получить проекты */
  getProjects: [data: OptionItemModel[]];
  /**Получить теги проекта */
  getTagsProject: [tags: TagModel[]];
  /**Получить НС проекта */
  getNamespacesProject: [namespaces: OptionItemModel[]];
  /**Получить тип НС */
  getTypesNamespace: [types: TypeModel[]];
  /**Получить сложности */
  getDifficultys: [difficulty: OptionItemModel[]];
  /**Получить очереди */
  getQueues: [queue: ProjectQueueTaskListItem[]];
  /**Получить статусы */
  getStatuses: [statuses: OptionItemModel[], isGlobalStatusChanged?: boolean];
  /**Получить приоритеты */
  getPriorities: [priorities: OptionItemModel[]];
}>();

/**Общее кол-во элементов в пункте */
const totalItemsCount = ref<TotalCountElementsItemFilter>({
  GlobalStatuses: 0,
  Difficultys: 0,
  Priorities: 0,
  Namespaces: 0,
  Statuses: 0,
  Projects: 0,
  Queues: 0,
  Tags: 0,
  Types: 0,
});

/**Кол-во выбранных элементов */
const countSelectElements = computed(
  () =>
    props.filterOptionData.SelectedOptions.length +
    (props.filterOptionData.HiddenElements[props.option]?.length || 0),
);
/**Состояние радиокнопки выбора всех элементов */
const stateAllElementsRadio = computed({
  get: () => (props.filterOptionData.SelectedOptions.length == 0 ? 1 : 0),
  set: () => emit('selectAllElements', props.option, false),
});
/**Состояние чекбокса выбора всех элементов */
const isSelectAllChecked = computed<boolean>({
  get: () => props.filterOptionData.SelectedOptions.length !== 0,
  set: async () => {
    emit('selectAllElements', props.option, true);
    if (props.option === OptionFilterEnum.GlobalStatuses && props.idSelectedQueue !== '0') {
      await getStatuses(
        props.idSelectedQueue,
        props.selectedGlobalStatuses.map((s) => String(s.Id)),
        undefined,
        true,
      );
    }
  },
});
/**Не выбраны элементы */
const notAllSelected = computed(
  () => props.filterOptionData?.SelectedOptions?.length !== props.filterOptionData?.Data?.length,
);
/**Отображение выбранных элементов на верху */
const sortedFilterData = computed(() => {
  const selectedIds = new Set(props.filterOptionData.SelectedOptions.map((item) => item.Id));
  return [...props.filterOptionData.Data].sort((a, b) => {
    const aSelected = selectedIds.has(a.Id);
    const bSelected = selectedIds.has(b.Id);

    return Number(!aSelected) - Number(!bSelected);
  });
});
/**Определяет, должен ли отображаться плейсхолдер по умолчанию  */
const isDefaultPlaceholderVisible = computed(
  () => props.option == OptionFilterEnum.Namespaces || props.option == OptionFilterEnum.Queues,
);
// Вычисляемое свойство, которое возвращает нужный текст при выборе всех элементов
const filterTextSelectedAllElem = computed(() => {
  return 'Все';
});

/**Отслеживание изменения поиска * */
watch(
  () => props.searchField,
  (value: string) => {
    search(value);
  },
);

/**После рендера компонента */
onMounted(async () => {
  switch (props.option) {
    case OptionFilterEnum.Tags:
      if (props.idSelectedProject) await getTagsProject(undefined, Number(props.idSelectedProject));
      break;
    case OptionFilterEnum.Namespaces:
      if (props.idSelectedProject)
        await getNamespacesProject(undefined, Number(props.idSelectedProject));
      break;
    case OptionFilterEnum.Queues:
      if (props.idSelectedProject) await getQueues(props.idSelectedNamespace, '');
      break;
    case OptionFilterEnum.Difficultys:
      if (props.idSelectedProject) await getDifficultys(props.idSelectedNamespace, '');
      break;
    case OptionFilterEnum.Priorities:
      if (props.idSelectedProject) await getPriorities(props.idSelectedQueue, '');
      break;
    case OptionFilterEnum.Types:
      if (props.idSelectedProject) await getTypes(props.idSelectedNamespace, '');
      break;
    case OptionFilterEnum.Statuses:
      if (props.idSelectedProject)
        await getStatuses(
          props.idSelectedQueue,
          props.selectedGlobalStatuses.map((s) => String(s.Id)),
          '',
        );
      break;
  }
});

/**Поиск */
const search = debounce((text: string) => {
  searchFilterElement(text);
}, 200);
/**Поиск по пунктам фильтра в зависимости от опции */
const searchFilterElement = async (search: string) => {
  switch (props.option) {
    case OptionFilterEnum.Projects:
      return await getProjects(search);
    case OptionFilterEnum.Tags:
      return await getTagsProject(search, props.idSelectedProject);
    case OptionFilterEnum.Namespaces:
      return await getNamespacesProject(search, props.idSelectedProject);
    case OptionFilterEnum.Difficultys:
      toggleItem(props.selectedGlobalStatuses[0], props.option, false);
      return await getDifficultys(props.idSelectedNamespace, search);
    case OptionFilterEnum.Queues:
      return await getQueues(props.idSelectedNamespace, search);
    case OptionFilterEnum.Types:
      return await getTypes(props.idSelectedNamespace, search);
    case OptionFilterEnum.Statuses:
      return await getStatuses(
        props.idSelectedQueue,
        props.selectedGlobalStatuses.map((s) => String(s.Id)),
        search,
      );
    case OptionFilterEnum.Priorities:
      return await getPriorities(props.idSelectedQueue, search);
  }
};
/** Получить список проектов */
const getProjects = async (searchText?: string) => {
  const response = await adapter.GetProjects(
    new ProjectSearchRequestModel({
      Name: searchText,
      Status: [EnumProjectType.Active],
      ShowOnlyCurrentIsAdmin: props.showOnlyCurrentIsAdmin,
      Page: new PageRequestModel({
        Skip: 0,
        Take: 10,
      }),
    }),
  );
  if (!response.IsSuccess) return;
  /**ID выбраных элементов проекта */
  const existingIds = new Set(props.filterOptionData.SelectedOptions.map((p) => p.Id));
  /**Выбранные элементы */
  const selectedElements = props.filterOptionData.SelectedOptions.filter((p) => p.Id !== 0).map(
    (p) => new ProjectModel({ ...p, PreviewColor: p.Color, ImageUrl: p.Image }),
  );

  let filteredProjects: OptionItemModel[] = [];

  filteredProjects = response.Value.Projects.filter((p) => !existingIds.has(p.Id)).map(
    (project: BaseProjectResponse) =>
      new OptionItemModel({
        Id: project.Id,
        Color: project.PreviewColor,
        Name: project.Name,
        Image: project.ImageUrl,
      }),
  );
  emit('getProjects', [...selectedElements, ...filteredProjects]);
  totalItemsCount.value.Projects = response.Value.Page.Count;
};
/**Получить следующий список проектов */
const getNextListProjects = async (search: string) => {
  if (totalItemsCount.value.Projects !== props.filterOptionData.Data.length) {
    const response = await adapter.GetNextListProjects(
      new ProjectSearchRequestModel({
        Name: search,
        Status: [EnumProjectType.Active],
        ShowOnlyCurrentIsAdmin: props.showOnlyCurrentIsAdmin,
        Page: new PageRequest({
          Skip: props.filterOptionData.Data.length,
          Take: 10,
        }),
      }),
    );
    if (response.IsSuccess) {
      props.filterOptionData.Data.push(
        ...response.Value.Projects.map(
          (project: BaseProjectResponse) =>
            new OptionItemModel({
              Id: project.Id,
              Color: project.PreviewColor,
              Name: project.Name,
              Image: project.ImageUrl,
            }),
        ),
      );
      totalItemsCount.value.Projects = response.Value.Page.Count;
    }
  }
};
/**Получить список тегов проекта */
const getTagsProject = async (search?: string, projectId?: number) => {
  const response = await adapter.getTags(
    new GetTagsListDetailRequest({
      ProjectId: projectId,
      Search: search,
      Page: new PageRequest({ Skip: 0, Take: 10 }),
    }),
  );

  if (response.IsSuccess) {
    const tags = response.Value.Items.flatMap((item) => item).map(
      (tag) => new TagModel({ ...tag, Name: tag?.TagName }),
    );
    emit('getTagsProject', tags);
    totalItemsCount.value.Tags = response.Value.Page.Count;
  }
};
/**Получить следующий список тегов проекта */
const getNextTagsProjectList = async (projectId: number, search?: string) => {
  if (totalItemsCount.value.Tags !== props.filterOptionData.Data.length) {
    const response = await adapter.getTags(
      new GetTagsListDetailRequest({
        ProjectId: projectId,
        Search: search,
        Page: new PageRequest({ Skip: props.filterOptionData.Data.length, Take: 10 }),
      }),
    );

    if (response.IsSuccess) {
      const tags = response.Value.Items.flatMap((item) => item).map(
        (tag) => new TagModel({ ...tag, Name: tag?.TagName }),
      );
      const existingIds = new Set(props.filterOptionData.Data.map((t) => t.Id));
      const unique = tags.filter((t) => !existingIds.has(t.Id));
      if (!notAllSelected.value) {
        props.filterOptionData.Data.push(...unique);
        props.filterOptionData.SelectedOptions.push(...unique);
      } else {
        props.filterOptionData.Data.push(...unique);
      }
      totalItemsCount.value.Tags = response.Value.Page.Count;
    }
  }
};
/**Получить список НС проекта */
const getNamespacesProject = async (search?: string, projectId?: number) => {
  const response = await adapter.getNamespaces(
    new ProjectNamespaceListRequest({
      Search: search,
      ShowOnlyCurrentIsAdmin: props.showOnlyCurrentIsAdmin,
      ProjectId: projectId,
      Page: new PageRequest({
        Skip: 0,
        Take: 10,
      }),
    }),
  );

  if (response.IsSuccess) {
    emit(
      'getNamespacesProject',
      response.Value.Items.map(
        (namespace) => new OptionItemModel({ ...namespace, Icon: namespace.IconUrl }),
      ),
    );
    totalItemsCount.value.Namespaces = response.Value.Page.Count;
  }
};
/**Получить следующий список НС */
const getNextListNamespaces = async (projectId: number, search: string) => {
  if (totalItemsCount.value.Namespaces !== props.filterOptionData.Data.length) {
    const response = await adapter.getNamespaces(
      new ProjectNamespaceListRequest({
        Search: search,
        ShowOnlyCurrentIsAdmin: props.showOnlyCurrentIsAdmin,
        ProjectId: projectId,
        Page: new PageRequest({
          Skip: props.filterOptionData.Data.length,
          Take: 10,
        }),
      }),
    );
    if (response.IsSuccess) {
      props.filterOptionData.Data.push(
        ...response.Value.Items.map(
          (namespace) => new OptionItemModel({ ...namespace, Icon: namespace.IconUrl }),
        ),
      );
      totalItemsCount.value.Namespaces = response.Value.Page.Count;
    }
  }
};
/**Получить список типов НС */
const getTypes = async (namespaceId: string, search?: string) => {
  const response = await adapter.getTypes(
    new GetListTaskTypeRequest({
      Search: search,
      NamespaceId: namespaceId,
      Page: new PageRequest({
        Skip: 0,
        Take: 10,
      }),
    }),
  );

  if (response.IsSuccess) {
    emit('getTypesNamespace', response.Value.Items);
    totalItemsCount.value.Types = response.Value.Page.Count;
  }
};
/**Получить следующий список типов НС */
const getNextTypesList = async (namespaceId: string, search?: string) => {
  if (totalItemsCount.value.Types !== props.filterOptionData.Data.length) {
    const response = await adapter.getTypes(
      new GetListTaskTypeRequest({
        Search: search,
        NamespaceId: namespaceId,
        Page: new PageRequest({
          Skip: props.filterOptionData.Data.length,
          Take: 10,
        }),
      }),
    );

    if (response.IsSuccess) {
      const existingIds = new Set(props.filterOptionData.Data.map((t) => t.Id));
      const unique = response.Value.Items.filter((t) => !existingIds.has(t.Id));
      if (!notAllSelected.value) {
        props.filterOptionData.Data.push(...unique);
        props.filterOptionData.SelectedOptions.push(...unique);
      } else {
        props.filterOptionData.Data.push(...unique);
      }
      totalItemsCount.value.Types = response.Value.Page.Count;
    }
  }
};
/**Получить список сложностей */
const getDifficultys = async (namespaceId: string, search?: string) => {
  const response = await adapter.getDifficultyListDetail(
    new GetDifficultyListDetailRequest({
      Search: search,
      NamespaceId: namespaceId,
      Page: new PageRequest({
        Skip: 0,
        Take: 10,
      }),
    }),
  );
  if (response.IsSuccess) {
    const difficultyList = response.Value.Items.map(
      (item) =>
        new OptionItemModel({
          Id: item.DifficultyId,
          Color: item.DifficultyColor,
          Name: item.DifficultyName,
        }),
    );
    emit('getDifficultys', difficultyList);
    totalItemsCount.value.Difficultys = response.Value.Page.Count;
  }
};
/**Получить следующий список сложностей */
const getNextDifficultyList = async (namespaceId: string, search: string) => {
  if (totalItemsCount.value.Difficultys !== props.filterOptionData.Data.length) {
    const response = await adapter.getDifficultyListDetail(
      new GetDifficultyListDetailRequest({
        Search: search,
        NamespaceId: namespaceId,
        Page: new PageRequest({
          Skip: props.filterOptionData.Data.length,
          Take: 10,
        }),
      }),
    );
    if (response.IsSuccess) {
      const difficultyList = response.Value.Items.map(
        (item) =>
          new OptionItemModel({
            Id: item.DifficultyId,
            Color: item.DifficultyColor,
            Name: item.DifficultyName,
          }),
      );
      const existingIds = new Set(props.filterOptionData.Data.map((d) => d.Id));
      const unique = difficultyList.filter((d) => !existingIds.has(d.Id));
      if (!notAllSelected.value) {
        props.filterOptionData.Data.push(...unique);
        props.filterOptionData.SelectedOptions.push(...unique);
      } else {
        props.filterOptionData.Data.push(...unique);
      }
      totalItemsCount.value.Difficultys = response.Value.Page.Count;
    }
  }
};
/**Получить список очередей */
const getQueues = async (namespaceId: string, search?: string) => {
  const response = await adapter.getQueue(
    new ProjectQueueTaskListRequest({
      Search: search,
      NamespacesId: [namespaceId],
      Page: new PageRequest({
        Skip: 0,
        Take: 10,
      }),
    }),
  );
  if (response.IsSuccess) {
    emit('getQueues', response.Value.Items);
    totalItemsCount.value.Queues = response.Value.Page.Count;
  }
};
/**Получить следующий список очередей */
const getNextQueuesList = async (namespaceId: string, search: string) => {
  if (totalItemsCount.value.Queues !== props.filterOptionData.Data.length) {
    const response = await adapter.getQueue(
      new ProjectQueueTaskListRequest({
        Search: search,
        NamespacesId: [namespaceId],
        Page: new PageRequest({
          Skip: props.filterOptionData.Data.length,
          Take: 10,
        }),
      }),
    );
    if (response.IsSuccess) {
      props.filterOptionData.Data.push(...response.Value.Items);
      totalItemsCount.value.Queues = response.Value.Page.Count;
    }
  }
};
/**Получить статусы */
const getStatuses = async (
  queueId: string,
  globalStatusesIds: string[],
  search?: string,
  isGlobalStatusChanged?: boolean,
) => {
  const response = await adapter.getStatusesListDetail(
    new GetListStatusRequest({
      QueueTasksId: [queueId],
      GlobalStatusIds: [],
      Search: search,
    }),
  );
  if (response.IsSuccess) {
    const statuses = response.Value.Items;

    emit('getStatuses', statuses, isGlobalStatusChanged);
    // totalItemsCount.value.Statuses = response.Value.Page.Count;
  }
};
/**Получить следующий список статусов */
// const getNextStatusesList = async (queueId: string, search: string) => {
//   if (totalItemsCount.value.Statuses !== props.filterOptionData.Data.length) {
//     const response = await adapter.getStatusesListDetail(
//       new GetStatusesListDetailRequest({
//         Search: search,
//         QueueTaskId: queueId,
//         Page: new PageRequest({
//           Skip: props.filterOptionData.Data.length,
//           Take: 10,
//         }),
//       }),
//     );
//     if (response.IsSuccess) {
//       const statuses = response.Value.Items.map(
//         ({ StatusId, StatusColor, StatusName }) =>
//           new OptionItemModel({
//             Id: StatusId,
//             Color: StatusColor,
//             Name: StatusName,
//           }),
//       );
//       const existingIds = new Set(props.filterOptionData.Data.map((d) => d.Id));
//       const unique = statuses.filter((d) => !existingIds.has(d.Id));
//       if (!notAllSelected.value) {
//         props.filterOptionData.Data.push(...unique);
//         props.filterOptionData.SelectedOptions.push(...unique);
//       } else {
//         props.filterOptionData.Data.push(...unique);
//       }
//       totalItemsCount.value.Difficultys = response.Value.Page.Count;
//     }
//   }
// };
/**Получить список приоритетов */
const getPriorities = async (queueId: string, search?: string) => {
  const response = await adapter.getPrioritiesListDetail(
    new GetPrioritiesListDetailRequest({
      Search: search,
      QueueTaskId: queueId,
      Page: new PageRequest({
        Skip: 0,
        Take: 10,
      }),
    }),
  );
  if (response.IsSuccess) {
    emit(
      'getPriorities',
      response.Value.Items.map(
        (item) =>
          new OptionItemModel({
            Id: item.PriorityId,
            Name: item.PriorityName,
            Color: item.PriorityColor,
          }),
      ),
    );
    totalItemsCount.value.Priorities = response.Value.Page.Count;
  }
};

/**Получить следующий список приоритетов */
const getNextPrioritiesList = async (queueId: string, search: string) => {
  if (totalItemsCount.value.Priorities !== props.filterOptionData.Data.length) {
    const response = await adapter.getPrioritiesListDetail(
      new GetPrioritiesListDetailRequest({
        Search: search,
        QueueTaskId: queueId,
        Page: new PageRequest({
          Skip: props.filterOptionData.Data.length,
          Take: 10,
        }),
      }),
    );
    if (response.IsSuccess) {
      const priorities = response.Value.Items.map(
        (item) =>
          new OptionItemModel({
            Id: item.PriorityId,
            Name: item.PriorityName,
            Color: item.PriorityColor,
          }),
      );
      const existingIds = new Set(props.filterOptionData.Data.map((p) => p.Id));
      const unique = priorities.filter((p) => !existingIds.has(p.Id));
      if (!notAllSelected.value) {
        props.filterOptionData.Data.push(...unique);
        props.filterOptionData.SelectedOptions.push(...unique);
      } else {
        props.filterOptionData.Data.push(...unique);
      }
      totalItemsCount.value.Priorities = response.Value.Page.Count;
    }
  }
};

/**Получить следующие данные */
const getNextData = async () => {
  switch (props.option) {
    case OptionFilterEnum.Projects:
      return await getNextListProjects(props.searchField);
    case OptionFilterEnum.Tags:
      return await getNextTagsProjectList(props.idSelectedProject, props.searchField);
    case OptionFilterEnum.Namespaces:
      return await getNextListNamespaces(props.idSelectedProject, props.searchField);
    case OptionFilterEnum.Difficultys:
      return await getNextDifficultyList(props.idSelectedNamespace, props.searchField);
    case OptionFilterEnum.Queues:
      return await getNextQueuesList(props.idSelectedNamespace, props.searchField);
    case OptionFilterEnum.Types:
      return await getNextTypesList(props.idSelectedNamespace, props.searchField);
    // case OptionFilterEnum.Statuses:
    //   return await getNextStatusesList(props.idSelectedQueue, props.searchField);
    case OptionFilterEnum.Priorities:
      return await getNextPrioritiesList(props.idSelectedQueue, props.searchField);
    default:
      return;
  }
};
/**Переключение чекбокса элемента*/
async function toggleItem(item: OptionItemModel, type: OptionFilterEnum, isMultiSelect: boolean) {
  emit('toggleItem', item, type, isMultiSelect);

  switch (type) {
    case OptionFilterEnum.GlobalStatuses:
      setTimeout(async () => {
        if (props.idSelectedQueue !== '0') {
          await getStatuses(
            String(props.idSelectedQueue),
            props.selectedGlobalStatuses.map((s) => String(s.Id)),
            undefined,
            true,
          );
        }
      });
      break;
    case OptionFilterEnum.Projects: {
      await getTagsProject(undefined, Number(item.Id));
      await getNamespacesProject(undefined, Number(item.Id));
      break;
    }
    case OptionFilterEnum.Namespaces:
      await getTypes(String(item.Id));
      await getDifficultys(String(item.Id));
      await getQueues(String(item.Id));
      break;
    case OptionFilterEnum.Queues:
      await getStatuses(
        String(item.Id),
        props.selectedGlobalStatuses.map((s) => String(s.Id)),
      );
      await getPriorities(String(item.Id));
      break;
  }
}

/**При скроле */
const onScroll = async (event) => {
  const scrollbar = event.target;

  const isEnd = scrollbar.scrollHeight - scrollbar.scrollTop <= scrollbar.clientHeight + 10;

  if (isEnd) {
    await getNextData();
  }
};
defineExpose({
  toggleItem,
});
</script>
<template>
  <div class="filter-parameter__header-block">
    <div v-if="filterOptionData.isMultiSelect">
      Выбрано:
      {{ countSelectElements }}
    </div>
    <div class="filter-parameter__select-all-block">
      <TirPmRadio
        v-if="!filterOptionData.isMultiSelect"
        v-model="stateAllElementsRadio"
        :value="1"
      />
      <TirCheckbox v-else v-model="isSelectAllChecked" :isUnselect="notAllSelected" @click.stop />
      <div v-if="filterOptionData.isMultiSelect">Выбрать все</div>
      <div v-else>{{ filterTextSelectedAllElem }}</div>
    </div>
  </div>
  <TirPmScrollbar class="filter-parameter__scrollbar" :show-on-hover="true" @onScroll="onScroll">
    <FilterParameterItem
      v-for="item in sortedFilterData"
      class="filter-parameter__item"
      :showComponent="showComponent"
      :typeParameter="typeParameter"
      :filterOptionData="filterOptionData"
      :key="item.Id"
      :item="item"
      :option="option"
      @toggleItem="toggleItem"
    />
  </TirPmScrollbar>
</template>
<style lang="scss" scoped>
.filter-parameter {
  &__scrollbar {
    max-height: 18rem;
  }
  &__header-block {
    margin-top: 0.75rem;
    & > div {
      padding: 0.75rem;
    }
  }
  &__select-all-block {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
  &__item {
    padding: 0.75rem;
  }
}
</style>
